import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';

import { bindAll } from 'bind-event-listener';
import debounce from 'lodash/debounce';
import omit from 'lodash/omit';
import { useDebouncedCallback } from 'use-debounce';

import { fg } from '@atlaskit/platform-feature-flags';
import { type Ati } from '@atlassian/ari/ati';

import { MOBILE_WIDTH_BREAKPOINT, RESIZING_DEBOUNCED } from './constants';
import type { CrossAppCreationConfig } from './types/crossAppCreation';
import type { UniversalCreateMenuSection, NativeMenuItem } from './types/menu';
import type { MixedMenuItems } from './types/menu-base';
import type { SuggestionConfig } from './types/suggestion';

/**
 * V2 version of findSelectedItem that searches through UniversalCreateMenuSection array.
 * Searches app-defined sections for items matching the selected ATI.
 * Note: Suggestion sections don't contain items at definition time, so they're skipped.
 *
 * @param sections - Array of UniversalCreateMenuSection to search through
 * @param selectedItem - The ATI to search for
 * @returns The matching NativeMenuItem or null if not found
 */
export function findSelectedItem(
	sections: UniversalCreateMenuSection[],
	selectedItem: Ati,
): NativeMenuItem | null {
	for (const section of sections) {
		// Only app-defined sections have items at definition time
		if (section.type === 'appDefinedSection') {
			for (const menuItem of section.items) {
				// Check if item has ati property (both NativeMenuItem and CrossAppMenuItem have it)
				if ('ati' in menuItem && menuItem.ati.toString() === selectedItem.toString()) {
					// For cross-app items, we need to return null as they don't have onClick/loadCreateContent
					// Only native items can be "selected" in the traditional sense
					if ('onClick' in menuItem || 'loadCreateContent' in menuItem) {
						return menuItem;
					}
				}
			}
		}
	}
	return null;
}

/**
 * Extracts all ATIs from the mixed menu items
 * @param mixedItems - The mixed menu items to extract ATIs from
 * @returns Array of ATI strings
 */
export function extractNativeAtis(mixedItems: MixedMenuItems): string[] {
	return mixedItems
		.flatMap((namedItem) => namedItem.items)
		.map((menuItem) => menuItem.ati.toString());
}

/**
 * SSR-safe implementation of {@link useIsResponsiveView}.
 *
 * Uses `useSyncExternalStore` so the value is derived directly from `window` on the client and
 * from a deterministic server snapshot (`false`) during SSR. This avoids both the SSR crash from
 * reading `window` at render time and the hydration mismatch caused by correcting the value inside
 * a `useEffect`: the server and the initial client render always produce the same markup, and the
 * client re-reads the real value on subscribe/resize.
 *
 * `enabled` is passed in by the public hook so both implementations can be called unconditionally
 * (preserving hook order). When disabled this hook is an inexpensive no-op: it returns `false` and
 * does not subscribe to `resize`.
 */
function useIsResponsiveViewSSR(enabled: boolean): boolean {
	const getSnapshot = useCallback(() => window.innerWidth <= MOBILE_WIDTH_BREAKPOINT, []);

	// During SSR (and the initial hydration render) `window` is unavailable, so we render as
	// non-responsive. The client re-reads the real value once subscribed, after hydration. When the
	// implementation is disabled we also report `false` and skip subscribing.
	const getServerSnapshot = useCallback(() => false, []);

	const subscribe = useCallback(
		(onStoreChange: () => void) => {
			if (!enabled) {
				return () => {};
			}

			const handleResizeDebounced = debounce(onStoreChange, RESIZING_DEBOUNCED);

			const unbind = bindAll(window, [
				{
					type: 'resize',
					listener: handleResizeDebounced,
				},
			]);

			return () => {
				handleResizeDebounced.cancel();
				unbind();
			};
		},
		[enabled],
	);

	return useSyncExternalStore(
		subscribe,
		enabled ? getSnapshot : getServerSnapshot,
		getServerSnapshot,
	);
}

/**
 * Legacy implementation of {@link useIsResponsiveView} that binds the resize listener in an effect.
 * Retained as the fallback while the SSR-safe rewrite is rolled out behind the
 * `navx_uc_responsive_ssr_fix` feature gate.
 *
 * The initial value is guarded against `window` being unavailable so that this hook can be called
 * unconditionally (alongside the SSR-safe variant) without crashing during server rendering.
 *
 * `enabled` is passed in by the public hook so both implementations can be called unconditionally
 * (preserving hook order). When disabled this hook is an inexpensive no-op: it returns `false` and
 * does not bind the `resize` listener.
 */
function useIsResponsiveViewLegacy(enabled: boolean): boolean {
	const [isResponsiveMode, setIsResponsiveMode] = useState(
		enabled && typeof window !== 'undefined' && window.innerWidth <= MOBILE_WIDTH_BREAKPOINT,
	);

	const [handleResizeDebounced] = useDebouncedCallback(() => {
		const isCurrentlyMobile = window.innerWidth <= MOBILE_WIDTH_BREAKPOINT;
		setIsResponsiveMode((prev) => (prev !== isCurrentlyMobile ? isCurrentlyMobile : prev));
	}, RESIZING_DEBOUNCED);

	useEffect(() => {
		if (!enabled) {
			return;
		}

		const unbind = bindAll(window, [
			{
				type: 'resize',
				listener: handleResizeDebounced,
			},
		]);

		return unbind;
	}, [enabled, handleResizeDebounced]);

	return enabled ? isResponsiveMode : false;
}

/**
 * Returns whether the current viewport is at or below the mobile breakpoint.
 *
 * Behind the `navx_uc_responsive_ssr_fix` feature gate this uses an SSR-safe
 * `useSyncExternalStore` implementation that avoids the hydration mismatch. When the gate is off it
 * falls back to the legacy effect-based implementation.
 *
 * Both implementations are called unconditionally so the hook call order stays stable across renders
 * (React Rules of Hooks, Must 1) — selecting between them with a ternary on `fg(...)` would call
 * hooks conditionally and can throw "Rendered more hooks than during the previous render" if the
 * gate value changes between renders. The gate result is passed into each implementation as
 * `enabled`, so the inactive one is an inexpensive no-op (returns `false`, binds no listeners).
 */
export function useIsResponsiveView(): boolean {
	const isSSREnabled = fg('navx_uc_responsive_ssr_fix');

	const isResponsiveSSR = useIsResponsiveViewSSR(isSSREnabled);
	const isResponsiveLegacy = useIsResponsiveViewLegacy(!isSSREnabled);

	return isSSREnabled ? isResponsiveSSR : isResponsiveLegacy;
}

// A safer omit, omitted properties must exist on the item type
export const omitSafely = <T extends object, P extends (keyof T)[]>(
	item: T,
	...properties: P
): Omit<T, P[number]> => omit(item, ...properties);

/**
 * Get the cloud ID from either V1 (suggestionConfig) or V2 (crossAppCreationConfig) configuration.
 */
export const getCloudId = (contextConfig: SuggestionConfig | CrossAppCreationConfig): string =>
	contextConfig.enabled ? contextConfig.cloudId : '';

/**
 * Get the org ID from either V1 (suggestionConfig) or V2 (crossAppCreationConfig) configuration.
 */
export const getOrgId = (
	contextConfig: SuggestionConfig | CrossAppCreationConfig,
): string | undefined => (contextConfig.enabled ? contextConfig.orgId : undefined);
